import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, getImageUrl } from "../lib/api";
import { Modal, Field, inputCls, PrimaryBtn, GhostBtn, DangerBtn } from "../components/DataTable";
import SiteSelector, { useSiteSelector } from "../components/SiteSelector";
import { useAuth } from "../context/AuthContext";
import { Map, Pencil, Eye } from "lucide-react";
import { toast } from "sonner";

const empty = { site_id: "", name: "", background_url: "", widgets: [] };

export default function MimicList() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "plant_admin", "admin"].includes(user?.role);
  const nav = useNavigate();
  const { sites, siteId, setSiteId } = useSiteSelector();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => siteId && api.get("/mimics", { params: { site_id: siteId } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [siteId]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/mimics", form);
      toast.success("Mimic created");
      setOpen(false); setForm(empty); load();
      nav(`/mimics/edit/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete mimic diagram?")) return;
    await api.delete(`/mimics/${id}`); load(); toast.success("Deleted");
  };

  const siteName = (id) => sites.find(s => s.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="scada-label">PLANT VISUALIZATION</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Mimic Diagrams</h1>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3">
            <SiteSelector sites={sites} siteId={siteId} setSiteId={setSiteId} />
            <PrimaryBtn data-testid="add-mimic-btn" onClick={() => { setForm({ ...empty, site_id: siteId || sites[0]?.id || "" }); setOpen(true); }}>+ Create Mimic</PrimaryBtn>
          </div>
        )}
        {!isAdmin && <SiteSelector sites={sites} siteId={siteId} setSiteId={setSiteId} />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
        {rows.length === 0 && (
          <div className="col-span-full scada-panel py-12 text-center text-[#5A6573] font-mono-metric text-sm">
            <Map className="inline mr-2" size={14} /> No mimic diagrams yet
          </div>
        )}
        {rows.map(m => (
          <div key={m.id} className="scada-panel p-0 group" data-testid={`mimic-card-${m.id}`}>
            <div className="h-40 border-b border-[#2C3238] relative overflow-hidden dot-grid" style={m.background_url ? { background: `#0B0D0F url(${getImageUrl(m.background_url)}) center/cover` } : {}}>
              <div className="absolute inset-0 bg-[#0B0D0F]/60" />
              <div className="absolute bottom-2 right-2 font-mono-metric text-[10px] text-[#9BA4B5]">{m.widgets?.length || 0} WIDGETS</div>
            </div>
            <div className="p-4">
              <div className="font-heading font-bold text-lg truncate">{m.name}</div>
              <div className="scada-label">{siteName(m.site_id)}</div>
              <div className="flex gap-1 mt-3">
                <Link to={`/mimics/view/${m.id}`} target="_blank" rel="noopener noreferrer" className="flex-1"><GhostBtn data-testid={`view-mimic-${m.id}`}><Eye className="inline mr-1" size={11} /> View</GhostBtn></Link>
                {isAdmin && <Link to={`/mimics/edit/${m.id}`}><GhostBtn data-testid={`edit-mimic-${m.id}`}><Pencil className="inline mr-1" size={11} /> Edit</GhostBtn></Link>}
                {isAdmin && <DangerBtn data-testid={`del-mimic-${m.id}`} onClick={() => remove(m.id)}>Del</DangerBtn>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Create Mimic Diagram" testId="mimic-modal">
        <form onSubmit={create}>
          <Field label="Site">
            <select required value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className={inputCls()} data-testid="mimic-site">
              <option value="">— select —</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls()} data-testid="mimic-name" /></Field>
          <Field label="Background Image URL (optional)"><input value={form.background_url} onChange={(e) => setForm({ ...form, background_url: e.target.value })} placeholder="https://..." className={inputCls()} /></Field>
          <div className="flex justify-end gap-2 mt-4"><PrimaryBtn type="submit" data-testid="save-mimic">Create & Edit</PrimaryBtn></div>
        </form>
      </Modal>
    </div>
  );
}
